import {
  PolygonStakeManagerAbi,
  StaderMaticxWithdrawalAbi,
} from '../abis/staderMaticx'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/**
 * Stader MaticX — per-user array enumeration on the Ethereum home
 * contract. Finalized when `IStakeManager(stakeManager).epoch() >=
 * requestEpoch + stakeManager.withdrawalDelay()`.
 *
 * Single stage: `getUserWithdrawalRequests(user)` returns the array
 * + `IStakeManager.epoch()` and `IStakeManager.withdrawalDelay()` for
 * finalization checks. Stake manager address is pinned in
 * `readerExtras.polygonStakeManager`.
 *
 * Per-request `readyAt` is a Polygon-checkpoint-based ETA, not a wall
 * clock. We surface it as the boolean `claimable` via
 * `requestEpoch + withdrawalDelay <= currentEpoch`; consumers wanting
 * an estimated time should map "checkpoints" → "seconds" off-chain.
 */
export const readerStaderMaticX: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []
    const stakeManager = entry.polygonStakeManager
    const stage1 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'getUserWithdrawalRequests',
          params: [user],
        },
        ...(stakeManager
          ? [
              { address: stakeManager, name: 'epoch', params: [] },
              { address: stakeManager, name: 'withdrawalDelay', params: [] },
            ]
          : []),
      ] as unknown as never[],
      abi: [
        StaderMaticxWithdrawalAbi,
        ...(stakeManager
          ? [PolygonStakeManagerAbi, PolygonStakeManagerAbi]
          : []),
      ] as unknown as never,
    })) as unknown[]

    const arr = stage1[0] as Array<Record<string, unknown>> | undefined
    if (!Array.isArray(arr)) return []
    const currentEpoch = stakeManager ? (toBigInt(stage1[1]) ?? 0n) : 0n
    const delay = stakeManager ? (toBigInt(stage1[2]) ?? 0n) : 0n

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < arr.length; i++) {
      const r = arr[i]
      const amount = toBigInt(r.amount) ?? 0n
      const requestEpoch = toBigInt(r.requestEpoch)
      if (requestEpoch === undefined) continue
      const ready = stakeManager ? currentEpoch >= requestEpoch + delay : false
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        // Use array index as the request id (consistent with
        // `claimWithdrawal(uint256 _idx)`).
        requestId: String(i),
        amountUnderlying: amount.toString(),
        status: ready ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
